/* HR — Settings (R7 fully wired)
 *
 *  Endpoints:
 *    GET    /api/settings              (任何登入者；is_secret=1 對非 admin 隱藏 value)
 *    GET    /api/settings/{key}
 *    POST   /api/settings              (admin only)
 *    PATCH  /api/settings/{key}        (admin only) → audit logged
 *    DELETE /api/settings/{key}        (admin only)
 *
 *  Settings 在後端以 TEXT 儲存，並提供 value_type hint ∈ { string, number, boolean, json }。
 *  前端依 value_type 顯示對應 widget；儲存時一律 stringify 回 TEXT。
 *
 *  分區由 category 欄位驅動。常見 category（依 enum_options.setting_category）：
 *    hr · finance · system · notification
 */

const CATEGORY_META = {
  hr:           { label: '人事',     icon: 'calendar' },
  finance:      { label: '財務',     icon: 'dollar-sign' },
  system:       { label: '系統',     icon: 'settings' },
  notification: { label: '通知',     icon: 'bell' },
  // fallback for未設 category 的：
  '':           { label: '未分類',   icon: 'folder' },
};

function castDisplay(value, valueType) {
  if (value == null) return '';
  if (valueType === 'boolean') return value === 'true' || value === '1' || value === true;
  if (valueType === 'number')  return value;  // 保留字串顯示，input type=number 會自轉
  if (valueType === 'json') {
    try { return JSON.stringify(JSON.parse(value), null, 2); }
    catch { return value; }
  }
  return value;
}

function stringifySave(value, valueType) {
  if (value == null) return null;
  if (valueType === 'boolean') return value ? 'true' : 'false';
  if (valueType === 'number')  return String(value);
  if (valueType === 'json') {
    try { return JSON.stringify(JSON.parse(value)); }  // re-stringify 確保格式
    catch { return value; }
  }
  return String(value);
}

function SettingField({ setting, value, onChange, dirty, isAdmin }) {
  const labelLines = (setting.key || '').split('.').slice(1).join('.') || setting.key;
  const valueType = setting.value_type || 'string';
  const redacted = setting.is_secret && value === null;

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '320px 1fr 90px', gap: 24,
      padding: '18px 0', borderTop: `1px solid ${HR.ruleSoft}`,
      alignItems: 'flex-start',
    }}>
      <div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: FontSizes.trading_md, color: HR.ink, fontWeight: 500,
        }}>
          {labelLines}
          {setting.is_secret && (
            <span style={{
              fontSize: FontSizes.trading_xxs, padding: '1px 6px', borderRadius: 2,
              background: HR.down, color: '#fff', fontWeight: 600,
              letterSpacing: '0.05em', fontFamily: HR.mono,
            }}>SECRET</span>
          )}
          {dirty && (
            <span style={{
              fontSize: FontSizes.trading_xxs, padding: '1px 6px', borderRadius: 2,
              background: HR.warn, color: '#fff', fontWeight: 600,
              letterSpacing: '0.05em', fontFamily: HR.mono,
            }}>DIRTY</span>
          )}
        </div>
        {setting.description && (
          <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, marginTop: 4, lineHeight: 1.5 }}>{setting.description}</div>
        )}
        <div style={{ fontSize: FontSizes.trading_md, color: HR.inkFaint, marginTop: 4, fontFamily: HR.mono, letterSpacing: '0.04em' }}>
          {setting.key}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {redacted ? (
          <span style={{ fontSize: FontSizes.xxs, color: HR.inkFaint, fontStyle: 'italic' }}>
            ●●●●●●（admin 限定可見）
          </span>
        ) : valueType === 'boolean' ? (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: isAdmin ? 'pointer' : 'not-allowed', fontSize: FontSizes.xs }}>
            <input
              type="checkbox"
              checked={!!value}
              disabled={!isAdmin}
              onChange={(e) => onChange(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: HR.accent }}
            />
            <span style={{ color: HR.inkSoft }}>{value ? '啟用' : '停用'}</span>
          </label>
        ) : valueType === 'number' ? (
          <input
            type="number"
            step="any"
            value={value ?? ''}
            disabled={!isAdmin}
            onChange={(e) => onChange(e.target.value)}
            style={inputStyle({ width: 200, fontFamily: HR.mono, textAlign: 'right' })}
          />
        ) : valueType === 'json' ? (
          <textarea
            value={value ?? ''}
            disabled={!isAdmin}
            onChange={(e) => onChange(e.target.value)}
            rows={4}
            style={{ ...inputStyle({ fontFamily: HR.mono, fontSize: FontSizes.xxs }), resize: 'vertical' }}
          />
        ) : (
          <TextInput value={value ?? ''} onChange={onChange} />
        )}
      </div>
      <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono, letterSpacing: '0.04em', alignSelf: 'center' }}>
        {valueType}
      </div>
    </div>
  );
}

function NewSettingModal({ onClose, onSaved }) {
  const toast = useToast();
  const [key, setKey] = React.useState('');
  const [category, setCategory] = React.useState('system');
  const [value_type, setValueType] = React.useState('string');
  const [description, setDescription] = React.useState('');
  const [value, setValue] = React.useState('');
  const [is_secret, setIsSecret] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    if (!key.trim()) { toast('key 必填', 'error'); return; }
    setSubmitting(true);
    try {
      await api('POST', '/settings', {
        key: key.trim(), value: value || null, value_type, category,
        description: description.trim() || null, is_secret,
      });
      toast('已新增', 'ok');
      onSaved();
    } catch (e) { toast('新增失敗：' + (e.detail || ''), 'error'); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal
      open onClose={onClose}
      title="新增系統設定"
      subtitle="New Setting Key"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>{submitting ? '建立中…' : '建立'}</Button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Key" required hint="dot.path 格式，例：hr.work_hours_per_day" span={2}>
          <TextInput value={key} onChange={setKey} mono placeholder="e.g. payroll.tax_rate" />
        </Field>
        <Field label="分類">
          <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle()}>
            {Object.entries(CATEGORY_META).filter(([k]) => k).map(([k, m]) => (
              <option key={k} value={k}>{m.label} ({k})</option>
            ))}
          </select>
        </Field>
        <Field label="型別">
          <select value={value_type} onChange={(e) => setValueType(e.target.value)} style={inputStyle()}>
            <option value="string">string</option>
            <option value="number">number</option>
            <option value="boolean">boolean</option>
            <option value="json">json</option>
          </select>
        </Field>
        <Field label="初始值" span={2}>
          {value_type === 'json' ? (
            <textarea value={value} onChange={(e) => setValue(e.target.value)} rows={3} style={{ ...inputStyle({ fontFamily: HR.mono, fontSize: FontSizes.xxs }), resize: 'vertical' }} placeholder='{ ... }' />
          ) : (
            <TextInput value={value} onChange={setValue} mono={value_type === 'number'} />
          )}
        </Field>
        <Field label="描述" span={2}>
          <Textarea value={description} onChange={setDescription} rows={2} />
        </Field>
        <Field label="機密值" span={2}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: FontSizes.xs, cursor: 'pointer' }}>
            <input type="checkbox" checked={is_secret} onChange={(e) => setIsSecret(e.target.checked)} style={{ width: 16, height: 16, accentColor: HR.accent }} />
            <span style={{ color: HR.inkSoft }}>標記為機密（value 對非 admin 隱藏）</span>
          </label>
        </Field>
      </div>
    </Modal>
  );
}

function SettingsPage() {
  const toast = useToast();
  const [user, setUser] = React.useState(null);
  const [settings, setSettings] = React.useState([]);  // raw from /settings
  const [values, setValues] = React.useState({});       // key → 顯示值
  const [original, setOriginal] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState('');
  const [showNew, setShowNew] = React.useState(false);

  // settings.write capability → admin only per matrix
  const canWrite = hasCapability(user, 'settings.write');
  const isAdmin = canWrite;  // 內部變數名保持 isAdmin 不改 — 對應 SettingField props

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [u, list] = await Promise.all([
        api('GET', '/auth/me').catch(() => null),
        api('GET', '/settings').catch(() => []),
      ]);
      setUser(u);
      setSettings(Array.isArray(list) ? list : []);
      const next = {};
      (list || []).forEach(s => { next[s.key] = castDisplay(s.value, s.value_type); });
      setValues(next);
      setOriginal({ ...next });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const dirtyKeys = React.useMemo(
    () => Object.keys(values).filter(k => values[k] !== original[k] && original[k] !== undefined),
    [values, original]
  );
  const isDirty = dirtyKeys.length > 0;

  const setOne = (k) => (v) => setValues(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    const settingByKey = {};
    settings.forEach(s => { settingByKey[s.key] = s; });
    let okCount = 0, failKeys = [];
    for (const k of dirtyKeys) {
      const spec = settingByKey[k];
      try {
        await api('PATCH', `/settings/${encodeURIComponent(k)}`, {
          value: stringifySave(values[k], spec?.value_type),
        });
        okCount += 1;
      } catch (e) {
        failKeys.push(k);
      }
    }
    setSaving(false);
    if (failKeys.length === 0) {
      toast(`已儲存 ${okCount} 項`, 'ok');
    } else {
      toast(`儲存 ${okCount} 項成功，${failKeys.length} 項失敗：${failKeys.join(', ')}`, 'error');
    }
    await load();
  };

  const discard = () => {
    setValues({ ...original });
    toast('已捨棄變更', 'info');
  };

  /* 依 category 分組 */
  const sections = React.useMemo(() => {
    const map = new Map();
    settings.forEach(s => {
      const cat = s.category || '';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(s);
    });
    // sort within section by key
    const out = [];
    for (const [cat, items] of map.entries()) {
      items.sort((a, b) => a.key.localeCompare(b.key));
      out.push({ key: cat, label: CATEGORY_META[cat]?.label || cat || '未分類', icon: CATEGORY_META[cat]?.icon || 'folder', items });
    }
    // category order: hr > finance > system > notification > others
    const order = ['hr', 'finance', 'system', 'notification'];
    out.sort((a, b) => {
      const ai = order.indexOf(a.key); const bi = order.indexOf(b.key);
      if (ai === -1 && bi === -1) return a.key.localeCompare(b.key);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    return out;
  }, [settings]);

  React.useEffect(() => {
    if (!activeSection && sections.length > 0) setActiveSection(sections[0].key);
  }, [sections, activeSection]);

  /* Scroll-spy */
  React.useEffect(() => {
    const onScroll = () => {
      const els = sections.map(s => ({
        key: s.key, el: document.getElementById(`settings-section-${s.key || 'misc'}`),
      })).filter(s => s.el);
      const scrollTop = window.scrollY + 200;
      let current = els[0]?.key;
      for (const s of els) {
        if (s.el.offsetTop <= scrollTop) current = s.key;
      }
      if (current !== activeSection) setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [sections, activeSection]);

  if (loading) {
    return (
      <>
        <PageHeader eyebrow="系統 · Configuration" title="系統設定" italic="Settings" />
        <SkeletonTable rows={6} />
      </>
    );
  }

  if (settings.length === 0) {
    return (
      <>
        <PageHeader eyebrow="系統 · Configuration" title="系統設定" italic="Settings" />
        <EmptyState
          icon="settings"
          title="系統尚未建立任何設定"
          hint={isAdmin ? '點下方按鈕新增第一筆。' : '請聯絡管理員初始化。'}
          action={isAdmin && <Button variant="primary" icon="plus" onClick={() => setShowNew(true)}>新增設定</Button>}
        />
        {showNew && <NewSettingModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="系統 · Configuration"
        title="系統設定"
        italic="Settings"
        action={
          dirtyCount > 0 && isAdmin ? (
            <Button variant="primary" icon="download" onClick={saveAll} disabled={saving}>
              {saving ? '儲存中…' : `儲存 ${dirtyCount} 項變更`}
            </Button>
          ) : null
        }
      />
      {!isAdmin && (
        <div style={{
          padding: '10px 14px', marginBottom: 18, fontSize: FontSizes.trading_md,
          background: HR.accentSoft, color: HR.accent, borderRadius: uiPick(4, GxT.radius.inner),
        }}>
          您目前以非 admin 角色檢視 — 機密值會被隱藏，且無法儲存變更。
        </div>
      )}

      {/* Sticky save bar */}
      {isDirty && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 5, marginBottom: 18,
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 20px',
          background: uiPick(HR.ink, GxT.color.inv), color: '#fff', borderRadius: uiPick(6, GxT.radius.inner),
          boxShadow: '0 8px 24px rgba(14,20,17,0.18)',
        }}>
          <span style={{ fontSize: FontSizes.xs, fontWeight: 500 }}>
            你有 <span style={{ fontFamily: HR.mono }}>{dirtyKeys.length}</span> 項變更未儲存
          </span>
          <div style={{ flex: 1 }} />
          <button
            onClick={discard} disabled={saving}
            style={{
              background: 'transparent', border: '1px solid rgba(255,255,255,0.25)',
              color: '#fff', padding: '6px 14px', borderRadius: uiPick(3, GxT.radius.btnSm), fontSize: FontSizes.trading_md, cursor: 'pointer',
            }}
          >捨棄</button>
          <button
            onClick={save} disabled={saving}
            style={{
              background: HR.accent, color: '#fff', border: 'none',
              padding: '6px 16px', borderRadius: uiPick(3, GxT.radius.btnSm), fontSize: FontSizes.trading_md, fontWeight: uiPick(500, GxT.weight.label),
              cursor: saving ? 'wait' : 'pointer',
            }}
          >{saving ? '儲存中…' : '儲存變更'}</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 32 }}>
        <nav style={{ position: 'sticky', top: isDirty ? 70 : 0, alignSelf: 'start' }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            fontSize: FontSizes.trading_sm, letterSpacing: uiPick('0.14em', 0), textTransform: uiPick('uppercase', 'none'),
            color: uiPick(HR.inkFaint, GxT.color.onV), padding: '0 12px 10px', fontWeight: uiPick(undefined, GxT.weight.label),
          }}>
            <span>設定分區</span>
            {isAdmin && (
              <button
                onClick={() => setShowNew(true)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: HR.accent, fontSize: FontSizes.md, padding: 0 }}
                title="新增設定"
              >＋</button>
            )}
          </div>
          {sections.map(s => {
            const sectionDirtyCount = s.items.filter(it => values[it.key] !== original[it.key]).length;
            const active = s.key === activeSection;
            return (
              <a
                key={s.key || 'misc'}
                href={`#settings-section-${s.key || 'misc'}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(`settings-section-${s.key || 'misc'}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', borderRadius: uiPick(4, GxT.radius.btnSm), fontSize: FontSizes.xs,
                  color: active ? HR.ink : HR.inkSoft,
                  background: active ? HR.surface : 'transparent',
                  border: active ? `1px solid ${HR.rule}` : '1px solid transparent',
                  fontWeight: active ? 500 : 400,
                  textDecoration: 'none', marginBottom: 2,
                }}
              >
                <Icon name={s.icon} size={13} />
                <span style={{ flex: 1 }}>{s.label}</span>
                {sectionDirtyCount > 0 && (
                  <span style={{
                    fontSize: FontSizes.trading_sm, padding: '1px 5px', borderRadius: 8,
                    background: HR.warn, color: '#fff', fontFamily: HR.mono,
                  }}>{sectionDirtyCount}</span>
                )}
              </a>
            );
          })}
        </nav>

        <div>
          {sections.map(s => (
            <div
              key={s.key || 'misc'}
              id={`settings-section-${s.key || 'misc'}`}
              style={{
                marginBottom: 40, scrollMarginTop: 80,
                background: HR.surface, border: uiPick(`1px solid ${HR.rule}`, 'none'),
                borderRadius: uiPick(8, GxT.radius.card), padding: '24px 28px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
                <Icon name={s.icon} size={16} />
                <h2 style={{
                  fontFamily: HR.serif, fontSize: FontSizes.lg, fontWeight: uiPick(500, GxT.weight.title), margin: 0,
                  letterSpacing: '-0.015em',
                }}>{s.label}</h2>
                <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono, letterSpacing: '0.06em' }}>
                  {s.items.length} 項
                </span>
              </div>
              {s.items.map(it => (
                <SettingField
                  key={it.key}
                  setting={it}
                  value={values[it.key]}
                  onChange={setOne(it.key)}
                  dirty={values[it.key] !== original[it.key]}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          ))}

          {/* === 薪資固定參數（payroll_config.json，read-only） === */}
          <PayrollConfigPanel />
        </div>
      </div>

      {showNew && <NewSettingModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </>
  );
}


/* ─────────────────────────  PayrollConfigPanel  ─────── */
/* 顯示 backend/payroll_config.json — 勞基法/費率/倍率/假日。Read-only。
 * 修改方式：admin 直接編 JSON 檔 → commit → deploy。 */

function PayrollConfigPanel() {
  const [cfg, setCfg] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api('GET', '/settings/payroll-config')
      .then(setCfg)
      .catch(() => setCfg(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!cfg) return null;

  const pct = (v) => (v * 100).toFixed(2) + '%';
  const m = cfg._meta || {};
  const year = new Date().getFullYear();
  const holidays = (cfg.holidays || {})[String(year)] || [];

  const Row = ({ label, value, hint }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '8px 0', borderBottom: `1px solid ${HR.ruleSoft}` }}>
      <div style={{ flex: 1, fontSize: FontSizes.xs, color: HR.ink }}>{label}</div>
      <div style={{ fontFamily: HR.mono, fontSize: FontSizes.xs, fontWeight: 600, color: HR.ink, textAlign: 'right', minWidth: 90 }}>{value}</div>
      {hint && <div style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, minWidth: 100 }}>{hint}</div>}
    </div>
  );

  const Group = ({ title, children }) => (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: FontSizes.trading_md, color: uiPick(HR.inkFaint, GxT.color.onV), letterSpacing: uiPick('0.12em', 0), textTransform: uiPick('uppercase', 'none'), fontWeight: uiPick(undefined, GxT.weight.label), marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );

  return (
    <div
      id="settings-section-payroll-config"
      style={{
        marginBottom: 40, scrollMarginTop: 80,
        background: HR.surface, border: uiPick(`1px solid ${HR.rule}`, 'none'),
        borderRadius: uiPick(8, GxT.radius.card), padding: '24px 28px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
        <Icon name="dollar-sign" size={16} />
        <h2 style={{ fontFamily: HR.serif, fontSize: FontSizes.lg, fontWeight: uiPick(500, GxT.weight.title), margin: 0, letterSpacing: '-0.015em' }}>
          薪資固定參數
        </h2>
        <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono, letterSpacing: '0.06em' }}>
          payroll_config.json · read-only
        </span>
        <span style={{ marginLeft: 'auto', fontSize: FontSizes.trading_md, padding: '3px 10px', background: HR.surfaceAlt, color: HR.inkSoft, borderRadius: uiPick(2, GxT.radius.chipSm), fontFamily: HR.mono }}>
          v{m.version || '—'} · effective {m.effective_from || '—'}
        </span>
      </div>
      <div style={{
        marginTop: 4, marginBottom: 18, padding: '10px 14px',
        background: HR.accentSoft, borderRadius: uiPick(4, GxT.radius.inner),
        fontSize: FontSizes.xxs, color: HR.accent, lineHeight: 1.5,
      }}>
        ℹ {m.edit_policy || '修改需 admin 直接編 JSON 檔。'}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
        <div>
          <Group title="基本參數">
            <Row label="投保薪資上限" value={`NT$ ${cfg.insurance_cap?.toLocaleString()}`} hint="勞健保 base 上限" />
            <Row label="月最低基本工資" value={`NT$ ${cfg.min_wage_monthly?.toLocaleString()}`} />
            <Row label="月計算日數" value={`${cfg.days_per_month} 天`} hint="勞基法 30 日制" />
            <Row label="每日工時" value={`${cfg.hours_per_day} 小時`} />
          </Group>

          <Group title="勞保費率（12.5%）">
            <Row label="普通事故" value={pct(cfg.labor_ins?.ordinary_rate)} />
            <Row label="就業保險" value={pct(cfg.labor_ins?.employment_rate)} />
            <Row label="員工負擔" value={pct(cfg.labor_ins?.emp_ratio)} hint="20%" />
            <Row label="雇主負擔" value={pct(cfg.labor_ins?.employer_ratio)} hint="70%" />
            <Row label="政府補助" value={pct(cfg.labor_ins?.gov_ratio)} hint="10%" />
          </Group>

          <Group title="健保費率（5.17%）">
            <Row label="費率" value={pct(cfg.health_ins?.rate)} />
            <Row label="員工負擔" value={pct(cfg.health_ins?.emp_ratio)} hint="× (1+眷屬)" />
            <Row label="雇主負擔" value={pct(cfg.health_ins?.employer_ratio)} />
          </Group>
        </div>

        <div>
          <Group title="二代健保">
            <Row label="補充保費費率" value={pct(cfg.second_health?.rate)} />
            <Row label="觸發倍率" value={`base × ${cfg.second_health?.threshold_multiplier}`} hint="超過扣 2.11%" />
          </Group>

          <Group title="勞退提繳">
            <Row label="提繳率" value={pct(cfg.labor_pension?.rate)} hint="雇主全額負擔" />
          </Group>

          <Group title="加班費倍率 (5 類)">
            <Row label="平日 前 2H" value={`× ${cfg.overtime?.weekday_h1}`} />
            <Row label="平日 第 3-4H" value={`× ${cfg.overtime?.weekday_h2}`} />
            <Row label="休息日 前 2H" value={`× ${cfg.overtime?.restday_h1}`} />
            <Row label="休息日 第 3H+" value={`× ${cfg.overtime?.restday_h2}`} />
            <Row label="例假/國定假日" value={`× ${cfg.overtime?.holiday}`} hint="公司政策" />
          </Group>

          <Group title="加班免稅門檻">
            <Row label="月加班免稅上限" value={`${cfg.overtime_tax_exempt?.monthly_hours_threshold} 小時`} hint="超過部分計稅" />
          </Group>

          <Group title={`${year} 年國定假日（${holidays.length} 天）`}>
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 6,
              fontFamily: HR.mono, fontSize: FontSizes.trading_lg, color: HR.inkSoft,
              maxHeight: 130, overflowY: 'auto',
            }}>
              {holidays.length === 0 ? (
                <span style={{ color: HR.inkFaint, fontStyle: 'italic' }}>無資料</span>
              ) : holidays.map(d => (
                <span key={d} style={{ padding: '2px 8px', background: HR.surfaceAlt, borderRadius: 2 }}>{d}</span>
              ))}
            </div>
          </Group>
        </div>
      </div>
    </div>
  );
}


window.SettingsPage = SettingsPage;
